import React, { useCallback, useState } from "react";
import { Layout, Page, Stack, Text } from "@shopify/polaris";
import { Field, Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import StepsButtonActions from "../../../../../common/StepsButtonActions/StepsButtonActions.js";
import SelectVariantCard from "./components/SelectVariantCard.js";
import AttributeSection from "./components/AttributeSection/AttributeSection.js";
import { useAppDispatch, useAppSelector } from "../../../../../../hooks/redux.js";
import useHandleKeyDown from "../../../../../../hooks/useHandleKeyDown.js";
import { useShopQuery } from "../../../../../../hooks/useShopQuery.js";
import { updateShop } from "../../../../../../store/features/shop.js";
import { nextStep, updateAttributesSelectedProducts } from "../../../../../../store/features/action.js";
import { openModal } from "../../../../../../store/features/modal.js";
import UsedProductsBanner from "../../../../../common/UsedProductsBanner/UsedProductsBanner.js";
import ValidationErrorBanner from "../../../../../common/ValidationErrorBanner/ValidationErrorBanner.js";
import { OPEN_MODAL_UPSELL } from "../../../../../../constants/modalTypes.js";
import { attributesProductSelectionTitles } from "../../../../../../helpers/attributesProductSelectionTitles.js";

const emptyPlan = {
  id: 0,
  name: "",
  productsLimit: 0,
  collectionsLimit: 0,
  monthlyPrice: 0,
  yearlyPricePerMo: 0,
  crossedPrice: 0,
  createdAt: new Date(),
  updatedAt: new Date(),
};

const AttributesSelectProducts = () => {
  const dispatch = useAppDispatch();
  const [productsCount, setProductsCount] = useState(0);
  const [isNextStepEnabled, setIsNextStepEnabled] = useState(false);
  const [isProductUsed, setIsProductUsed] = useState(false);
  const [isBannerChecked, setIsBannerChecked] = useState(false);
  const [bannerValidError, setBannerValidError] = useState(false);
  const [isSubmitAttempted, setIsSubmitAttempted] = useState(false);
  const attributesSelectedProducts = useAppSelector((state) => state.action.actionData.selectedProducts.attributesSelectedProducts);
  const { isFetching, refetch } = useShopQuery({
    onSuccess: (shopData) => dispatch(updateShop(shopData)),
    enabled: false,
  });

  const onSubmit = useCallback(async (data) => {
    if (!isBannerChecked && isProductUsed) {
      setBannerValidError(true)
      return;
    }

    setIsSubmitAttempted(true);
    if (!productsCount) return;

    dispatch(updateAttributesSelectedProducts(data));
    const { data: shopData } = await refetch();
    if (shopData) {
      const currentPlan = shopData.subscriptionPlan ?? emptyPlan;

      if (productsCount > currentPlan.productsLimit) {
        dispatch(openModal({
          modalType: OPEN_MODAL_UPSELL,
          modalData: {
            limitProductCount: productsCount,
            limitCollectionCount: 0,
          },
        }));
      } else {
        dispatch(nextStep());
      }
    }
  }, [dispatch, refetch, productsCount, isBannerChecked, isProductUsed]);

  const handleKeyDown = useHandleKeyDown();

  const getTitleBasedOnSelection = (selection) => attributesProductSelectionTitles[selection]

  return (
    <Page fullWidth>
      {isProductUsed && productsCount > 0 && <UsedProductsBanner setIsBannerChecked={setIsBannerChecked} setIsProductUsed={setIsProductUsed} bannerValidError={bannerValidError} setBannerValidError={setBannerValidError} />}
      <Form
        onSubmit={onSubmit}
        // @ts-ignore
        mutators={{
          ...arrayMutators,
          setFormAttribute: ([fieldName, fieldVal], state, { changeValue }) => {
            changeValue(state, fieldName, () => fieldVal);
          },
        }}
        initialValues={attributesSelectedProducts}
        render={({ form, handleSubmit, values }) => {
          return (
            <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
              <Stack vertical>
                <ValidationErrorBanner
                  message="Select at least one product before proceeding to the next step."
                  show={isSubmitAttempted && !productsCount}
                />
                <StepsButtonActions disabled={false} isNextLoading={isFetching} />
                <Layout>
                  <Layout.Section secondary>
                    <Stack vertical>
                      <Text variant="headingLg" as="p">
                        Select your products
                      </Text>
                      <Text variant="bodySm" as="p">
                        Target your products based on different attributes. Those can include
                        missing pictures, descriptions and a lot more.
                      </Text>
                    </Stack>
                  </Layout.Section>
                  <Layout.Section>
                    <Field name={"selectProductsBasedOn"} component={SelectVariantCard} />
                  </Layout.Section>
                </Layout>
                <Layout>
                  <Layout.Section secondary>
                    <Stack vertical>
                      <Text variant="headingLg" as="p">
                        {getTitleBasedOnSelection(values.selectProductsBasedOn)}
                      </Text>
                    </Stack>
                  </Layout.Section>
                  <Layout.Section>
                    <AttributeSection
                      values={values}
                      setFormAttribute={form.mutators.setFormAttribute}
                      productsCount={productsCount}
                      setProductsCount={setProductsCount}
                      setIsProductUsed={setIsProductUsed}
                    />
                  </Layout.Section>
                </Layout>
              </Stack>
            </form>
          );
        }}
      />
    </Page>
  );
};

export default AttributesSelectProducts;